import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {formEncodedApi} from 'nutshell-core/api';

import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {Link} from 'shells/link/link';
import {PlanUpgradeLink} from 'shells/upgrade/plan-upgrade-link';

import type {GetNutshellMarketingBannerInfo as GetNutshellMarketingBannerInfoQuery} from 'nutshell-graphql-types';

import GET_BANNER_INFO from './graphql/queries/get-banner-info.graphql';

type Props = {
    isImportUnsubscribesBannerNeeded?: boolean;
};

export function MarketingStatsBanner(props: Props) {
    const {data} = useQuery<GetNutshellMarketingBannerInfoQuery, Record<any, any>>(
        GET_BANNER_INFO,
        {
            notifyOnNetworkStatusChange: true,
        }
    );

    const nutshellMarketingStats = data ? data.nutshellMarketingStats : null;

    const error =
        nutshellMarketingStats && nutshellMarketingStats.gate.error
            ? nutshellMarketingStats.gate.error
            : undefined;

    const shouldShowImportUnsubscribesBanner =
        props.isImportUnsubscribesBannerNeeded &&
        !NutClientConfig.hasDismissedImportUnsubscribedWarning &&
        nutshellMarketingStats &&
        nutshellMarketingStats.shouldShowImportUnsubscribesBanner;

    if (error) {
        return (
            <div className='mb-8'>
                <FlashBanner type='warning' hasBorder={true}>
                    <div className='full-width flex justify-sb'>
                        <span>{error.message}</span>
                        {/* If marketing is disabled, don’t show cta to upgrade */}
                        {error.reason === 'REASON_MARKETING_DISABLED' ? undefined : (
                            <PlanUpgradeLink />
                        )}
                    </div>
                </FlashBanner>
            </div>
        );
    }

    if (shouldShowImportUnsubscribesBanner) {
        return (
            <div className='mb-16'>
                <FlashBanner type='info' hasBorder={true} onHidden={handleHidden}>
                    <div className='inline-block'>
                        <span style={{fontWeight: 'bold'}}>
                            It looks like you’re ready to send your first message!
                        </span>{' '}
                        We recommend importing unsubscribers into Nutshell before sending this
                        broadcast, or your message might result in a high bounce rate.{' '}
                        <Link
                            variant='primary'
                            href='https://support.nutshell.com/en/articles/8428874-audiences-unsubscribe-contacts-from-campaigns'
                            newTab={true}
                        >
                            Learn how to update unsubscribes
                        </Link>
                    </div>
                </FlashBanner>
            </div>
        );
    }

    return null;
}

const handleHidden = () => {
    NutClientConfig.hasDismissedImportUnsubscribedWarning = true;
    formEncodedApi.post('/my-account/save-user-setting.json', {
        setting: 'has_dismissed_import_unsubscribed_warning',
        value: 1,
    });
};
